import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { LANGUAGES, LANGUAGE_LABELS, useI18n } from '../lib/i18n';

const LOGO_SRC = '/brand/logo.png';
const LOGO_MASK = '/brand/logo-mask.png';

/**
 * The Auditoria mark. `tone="mark"` keeps the original brushed-metal artwork;
 * any other tone masks the shape so it can take a flat colour, which the
 * artwork itself cannot do.
 */
export function Logo({
  size = 'md',
  tone = 'mark',
  withWordmark = false,
  className = '',
}: {
  size?: 'sm' | 'md' | 'lg';
  tone?: 'mark' | 'brand' | 'ink';
  withWordmark?: boolean;
  className?: string | undefined;
}) {
  const heights = { sm: 'h-6', md: 'h-8', lg: 'h-11' } as const;
  const colors = { brand: 'bg-brand', ink: 'bg-ink', mark: '' } as const;

  const mark =
    tone === 'mark' ? (
      <img
        src={LOGO_SRC}
        alt="Auditoria Detailing"
        className={`${heights[size]} w-auto select-none`}
        draggable={false}
      />
    ) : (
      <span
        role="img"
        aria-label="Auditoria Detailing"
        className={`${heights[size]} ${colors[tone]} block w-auto`}
        style={{
          aspectRatio: '900 / 288',
          maskImage: `url(${LOGO_MASK})`,
          WebkitMaskImage: `url(${LOGO_MASK})`,
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
        }}
      />
    );

  if (!withWordmark) return <span className={`inline-flex ${className}`}>{mark}</span>;

  return (
    <span className={`inline-flex items-center gap-3 ${className}`}>
      {mark}
      <span className="leading-tight">
        <span className="block text-[0.78rem] font-bold tracking-[0.2em] text-ink">AUDITORIA</span>
        <span className="block text-[0.58rem] font-medium tracking-[0.34em] text-ink-faint">
          DETAILING
        </span>
      </span>
    </span>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'ghost' | 'quiet' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

const BUTTON_VARIANTS = {
  primary: 'bg-brand text-black shadow-[0_1px_0_rgba(255,255,255,0.15)_inset] hover:bg-brand-soft active:translate-y-px',
  ghost: 'border border-line bg-raised text-ink hover:border-ink-faint hover:bg-muted active:translate-y-px',
  quiet: 'text-ink-dim hover:bg-raised hover:text-ink',
  danger: 'bg-danger text-white hover:opacity-90 active:translate-y-px',
} as const;

/*
 * Heights are set explicitly rather than left to padding: the staff portal is
 * used on a phone at the counter, and a 44px target is the smallest a thumb
 * hits reliably. Desktop tightens the dense sizes back up.
 */
const BUTTON_SIZES = {
  sm: 'min-h-11 px-3.5 text-xs rounded-lg gap-1.5 sm:min-h-8 sm:px-3',
  md: 'min-h-11 px-4 text-sm rounded-xl gap-2 sm:min-h-10',
  lg: 'min-h-12 px-5 text-base rounded-xl gap-2',
} as const;

export function Button({
  variant = 'primary', size = 'md', loading, children, className = '', ...rest
}: ButtonProps) {
  return (
    <button
      className={`inline-flex items-center justify-center font-semibold transition-all duration-150 disabled:pointer-events-none disabled:opacity-40 ${BUTTON_VARIANTS[variant]} ${BUTTON_SIZES[size]} ${className}`}
      disabled={loading || rest.disabled}
      {...rest}
    >
      {loading && (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}

const CONTROL =
  'w-full min-w-0 rounded-xl border bg-raised px-3.5 py-2.5 text-ink transition-colors duration-150 placeholder:text-ink-faint focus:border-brand focus:bg-muted focus:outline-none';

export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string | undefined }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-ink-faint"
    >
      {children}
    </label>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | undefined;
  hint?: string | undefined;
}

export function Field({ label, error, hint, id, className = '', ...rest }: FieldProps) {
  const fieldId = id ?? rest.name ?? label?.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {label && <Label htmlFor={fieldId}>{label}</Label>}
      <input
        id={fieldId}
        aria-invalid={error ? true : undefined}
        className={`${CONTROL} ${error ? 'border-danger' : 'border-line'} ${className}`}
        {...rest}
      />
      {error && <p role="alert" className="text-xs text-danger">{error}</p>}
      {!error && hint && <p className="text-xs text-ink-faint">{hint}</p>}
    </div>
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  /** For a label that is only wanted at some widths. */
  labelClassName?: string;
}

/** Native select with the browser chrome removed, so it matches the dark UI. */
export function Select({
  label, labelClassName, id, className = '', children, ...rest
}: SelectProps) {
  const fieldId = id ?? rest.name ?? label?.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {label && (
        <span className={labelClassName}>
          <Label htmlFor={fieldId}>{label}</Label>
        </span>
      )}
      <div className="relative">
        <select
          id={fieldId}
          className={`${CONTROL} appearance-none truncate border-line pr-9 ${className}`}
          {...rest}
        >
          {children}
        </select>
        <svg
          aria-hidden
          viewBox="0 0 12 8"
          className="pointer-events-none absolute right-3.5 top-1/2 h-2 w-3 -translate-y-1/2 text-ink-faint"
        >
          <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}

interface MoneyInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  hint?: string | undefined;
}

/** Amount entry with the lari sign inside the control and no spinner buttons. */
export function MoneyInput({ label, hint, id, className = '', ...rest }: MoneyInputProps) {
  const fieldId = id ?? rest.name ?? label?.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {label && <Label htmlFor={fieldId}>{label}</Label>}
      <div className="relative">
        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-ink-faint">
          ₾
        </span>
        <input
          id={fieldId}
          inputMode="decimal"
          autoComplete="off"
          className={`${CONTROL} tabular border-line pl-7 text-right ${className}`}
          {...rest}
        />
      </div>
      {hint && <p className="text-right text-xs text-ink-faint">{hint}</p>}
    </div>
  );
}

/**
 * Percentage entry. Stores basis points so the value stays an exact integer.
 *
 * What the operator is typing is held separately from the number it parses to.
 * Rendering `valueBp / 100` straight back would delete the decimal point the
 * moment it was typed — "5." parses to 5, which renders as "5" — leaving no way
 * to enter 5.5%.
 */
export function PercentInput({
  label, valueBp, onChange, hint, max = 100,
}: {
  label?: string;
  valueBp: number;
  onChange: (next: number) => void;
  hint?: string | undefined;
  max?: number;
}) {
  const [text, setText] = useState(() => (valueBp === 0 ? '' : String(valueBp / 100)));

  // Follow the value when it is changed from outside, but never while the
  // operator's own text still parses to it.
  useEffect(() => {
    const typed = text.trim().replace(',', '.');
    const parsed = typed === '' ? 0 : Math.round(Number(typed) * 100);
    if (parsed !== valueBp) setText(valueBp === 0 ? '' : String(valueBp / 100));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueBp]);

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {label && <Label>{label}</Label>}
      <div className="relative">
        <input
          inputMode="decimal"
          autoComplete="off"
          value={text}
          placeholder="0"
          onChange={(event) => {
            const next = event.target.value;
            if (!/^[0-9]*[.,]?[0-9]{0,2}$/.test(next)) return;
            setText(next);

            const typed = next.trim().replace(',', '.');
            if (typed === '' || typed === '.') { onChange(0); return; }
            const percent = Number(typed);
            if (!Number.isFinite(percent) || percent < 0) return;
            onChange(Math.min(max * 100, Math.round(percent * 100)));
          }}
          onBlur={() => setText(valueBp === 0 ? '' : String(valueBp / 100))}
          className={`${CONTROL} tabular border-line pr-8 text-right`}
        />
        <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-ink-faint">
          %
        </span>
      </div>
      {hint && <p className="text-right text-xs text-ink-faint">{hint}</p>}
    </div>
  );
}

export function Stepper({
  value, onChange, min = 1, max = 999, label,
}: {
  value: number; onChange: (next: number) => void;
  min?: number; max?: number; label?: string;
}) {
  const { t } = useI18n();
  const step = (delta: number) => onChange(Math.min(max, Math.max(min, value + delta)));
  const button = 'grid h-11 w-11 place-items-center text-lg text-ink-dim transition hover:bg-muted hover:text-ink disabled:opacity-30 sm:h-10 sm:w-9 sm:text-base';
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label>{label ?? t('ui.qty')}</Label>
      <div className="flex items-center overflow-hidden rounded-xl border border-line bg-raised">
        <button type="button" onClick={() => step(-1)} disabled={value <= min} aria-label={t('ui.decrease')} className={button}>
          −
        </button>
        <span className="tabular min-w-9 px-1 text-center text-sm font-semibold text-ink">{value}</span>
        <button type="button" onClick={() => step(1)} disabled={value >= max} aria-label={t('ui.increase')} className={button}>
          +
        </button>
      </div>
    </div>
  );
}

export function Alert({ tone = 'danger', children }: { tone?: 'danger' | 'warning' | 'info' | 'success'; children: ReactNode }) {
  const styles = {
    danger: 'border-danger/35 bg-danger/10',
    warning: 'border-warning/35 bg-warning/10',
    info: 'border-line bg-raised',
    success: 'border-positive/35 bg-positive/10',
  } as const;
  return (
    <div role="alert" className={`rounded-xl border px-3.5 py-3 text-sm text-ink ${styles[tone]}`}>
      {children}
    </div>
  );
}

export function Card({
  children, className = '', padded = true,
}: { children: ReactNode; className?: string | undefined; padded?: boolean }) {
  return (
    <div className={`rounded-2xl border border-line bg-surface ${padded ? 'p-5' : ''} ${className}`}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode | undefined }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">{children}</h2>
      {action}
    </div>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode | undefined }) {
  return (
    <Card className="min-w-0">
      <div className="text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-ink-faint sm:text-[0.68rem]">{label}</div>
      {/* Two to a row on a phone, so a long sum gets a size that fits half the screen. */}
      <div className="tabular mt-2 text-[1.25rem] font-bold leading-none text-ink [overflow-wrap:anywhere] sm:text-[1.6rem]">{value}</div>
      {sub && <div className="mt-2 text-[0.7rem] leading-snug text-ink-dim sm:text-xs">{sub}</div>}
    </Card>
  );
}

const TIER_STYLES: Record<string, string> = {
  BRONZE: 'text-bronze border-bronze/35 bg-bronze/10',
  SILVER: 'text-silver border-silver/35 bg-silver/10',
  GOLD: 'text-gold border-gold/35 bg-gold/10',
  PLATINUM: 'text-platinum border-platinum/35 bg-platinum/10',
};

export function TierBadge({ code, size = 'md' }: { code: string; size?: 'sm' | 'md' }) {
  return (
    <span
      className={`shrink-0 rounded-md border font-bold tracking-[0.14em] ${
        size === 'sm' ? 'px-1.5 py-0.5 text-[0.58rem]' : 'px-2 py-0.5 text-[0.65rem]'
      } ${TIER_STYLES[code] ?? 'border-line bg-raised text-ink-dim'}`}
    >
      {code}
    </span>
  );
}

export function Pill({ tone = 'neutral', children }: { tone?: 'neutral' | 'danger' | 'warning' | 'positive' | undefined; children: ReactNode }) {
  const styles = {
    neutral: 'border-line bg-raised text-ink-faint',
    danger: 'border-danger/35 bg-danger/10 text-danger',
    warning: 'border-warning/35 bg-warning/10 text-warning',
    positive: 'border-positive/35 bg-positive/10 text-positive',
  } as const;
  return (
    <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[0.58rem] font-semibold tracking-[0.12em] ${styles[tone]}`}>
      {children}
    </span>
  );
}

/**
 * Large amounts. The lari sign only exists in the Georgian fallback font, so
 * setting it smaller and dimmer turns an unavoidable weight mismatch into a
 * deliberate typographic treatment.
 */
export function Amount({
  value, className = '', symbolClassName = '',
}: { value: string; className?: string | undefined; symbolClassName?: string | undefined }) {
  const negative = value.startsWith('-');
  const digits = value.replace(/^-?\u20BE/, '');
  return (
    <span className={`tabular tracking-tight ${className}`}>
      {negative && '−'}
      <span className={`mr-[0.08em] align-[0.08em] text-[0.58em] font-semibold text-ink-faint ${symbolClassName}`}>
        ₾
      </span>
      {digits}
    </span>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl border border-line bg-surface ${className}`} />;
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string | undefined; action?: ReactNode }) {
  return (
    <Card className="py-10 text-center">
      <p className="text-sm font-medium text-ink-dim">{title}</p>
      {hint && <p className="mt-1.5 text-xs text-ink-faint">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </Card>
  );
}

/**
 * Each person picks their own language; it is stored in their browser.
 *
 * Also sits on the sign-in screen: the default is Russian, so an owner who
 * reads English would otherwise have to sign in before being able to switch.
 */
export function LanguagePicker({ className = '' }: { className?: string }) {
  const { language, setLanguage } = useI18n();
  return (
    <div className={`flex gap-1 ${className}`}>
      {LANGUAGES.map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLanguage(code)}
          aria-pressed={language === code}
          className={`min-h-9 flex-1 rounded-lg px-2 text-xs font-medium transition-colors ${
            language === code
              ? 'bg-raised text-ink'
              : 'text-ink-faint hover:bg-raised/60 hover:text-ink-dim'
          }`}
        >
          {LANGUAGE_LABELS[code]}
        </button>
      ))}
    </div>
  );
}

/**
 * A "?" next to a label that opens a sentence of plain explanation.
 *
 * Expands in place rather than floating over the page: a tooltip that follows
 * the cursor is unreadable on the phone most of this shift is spent holding,
 * and it cannot be left open while you change the thing it describes.
 */
export function HelpNote({ text }: { text: string }) {
  const [place, setPlace] = useState<{ top: number; left: number; above: boolean } | null>(null);
  const mark = useRef<HTMLButtonElement | null>(null);
  const { t } = useI18n();

  /*
   * Shown on hover and hidden the moment the pointer leaves — it is a hint,
   * not a panel. Focus does the same from the keyboard. A touch screen has
   * neither hover nor, on iOS, focus on a tapped button, so there a tap opens
   * it and a tap anywhere else closes it.
   *
   * Placed in the window's coordinates and kept inside it: a bubble centred
   * on a mark near the edge of a phone screen would otherwise hang off it.
   */
  const BUBBLE = 256;
  const MARGIN = 12;
  const show = (): void => {
    const rect = mark.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(BUBBLE, window.innerWidth - MARGIN * 2);
    const centre = rect.left + rect.width / 2;
    const left = Math.min(Math.max(centre - width / 2, MARGIN), window.innerWidth - width - MARGIN);
    const above = window.innerHeight - rect.bottom < 160;
    setPlace({ top: above ? rect.top - 8 : rect.bottom + 8, left, above });
  };
  const hide = (): void => setPlace(null);
  const touch = (): boolean => window.matchMedia('(hover: none)').matches;

  useEffect(() => {
    if (!place) return undefined;
    const away = (event: Event) => {
      if (!mark.current?.contains(event.target as Node)) hide();
    };
    document.addEventListener('pointerdown', away);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('pointerdown', away);
      window.removeEventListener('scroll', hide, true);
    };
  }, [place]);

  return (
    <span className="relative inline-flex">
      <button
        ref={mark}
        type="button"
        aria-label={t('common.whatIsThis')}
        aria-expanded={place !== null}
        onMouseEnter={() => { if (!touch()) show(); }}
        onMouseLeave={() => { if (!touch()) hide(); }}
        onFocus={() => { if (!touch()) show(); }}
        onBlur={hide}
        onClick={() => { if (touch()) { if (place) hide(); else show(); } }}
        // The visible mark stays small; the padding around it is what a thumb hits.
        className="-m-3 grid shrink-0 place-items-center p-3"
      >
        <span className="grid size-[1.15rem] place-items-center rounded-full border border-line text-[0.68rem] font-semibold leading-none text-ink-faint transition-colors hover:border-ink-faint hover:text-ink-dim">
          ?
        </span>
      </button>

      {place && createPortal(
        <span
          role="tooltip"
          style={{ top: place.top, left: place.left, width: Math.min(BUBBLE, window.innerWidth - MARGIN * 2) }}
          className={`pointer-events-none fixed z-[70] rounded-xl border border-line bg-raised px-3 py-2 text-[0.72rem] font-normal normal-case leading-relaxed tracking-normal text-ink-dim shadow-lg ${
            place.above ? '-translate-y-full' : ''
          }`}
        >
          {text}
        </span>,
        document.body,
      )}
    </span>
  );
}
